import { FunctionComponent, useState } from 'react';
import { Box, Stack, SvgIcon, Tab, Typography, useTheme } from '@mui/material';
import ArrowLeftIcon from '@heroicons/react/24/solid/ArrowLeftIcon';
import { useParams } from 'react-router-dom';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useGetProductConfigurationQuery } from '@/api/v2/productConfigurations';
import { ProductConfigurationGeneralConfiguration } from '@/pageContents/productConfiguration/ProductConfigurationDetail/ProductConfigurationGeneralConfiguration';
import Link from '@mui/material/Link';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { ContingentConditionConfiguration } from '@/pageContents/productConfiguration/ProductConfigurationDetail/conditions/ContingentConditionConfiguration';
import { PriceConditionConfiguration } from '@/pageContents/productConfiguration/ProductConfigurationDetail/conditions/PriceConditionConfiguration';

type TabValue = 'general' | 'priceCondition' | 'contingentCondition';

export const ProductConfigurationDetailContent: FunctionComponent = () => {
    const theme = useTheme();
    const [tabValue, setTabValue] = useState<TabValue>('general');
    const { id } = useParams();
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const { data: productConfiguration } = useGetProductConfigurationQuery({
        liftOperatorId,
        productId: id!,
    });

    const handleTabChange = (_, newValue: TabValue) => {
        setTabValue(newValue);
    };

    return (
        <TabContext value={tabValue}>
            <Link
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: 'text.primary',
                    mb: 6,
                }}
                href='/produktkonfiguration'
            >
                <SvgIcon
                    fontSize='small'
                    sx={{
                        mr: 2,
                    }}
                >
                    <ArrowLeftIcon />
                </SvgIcon>

                <Typography variant='subtitle2' component='span'>
                    Produktkonfiguration
                </Typography>
            </Link>

            {productConfiguration && (
                <Stack spacing={theme.page.verticalSpacing}>
                    <Typography variant='h4'>
                        Konfiguration für alle Tickets mit hinterlegter
                        Ticketbezeichnung:{' '}
                        <Typography
                            variant='h4'
                            component='span'
                            sx={(theme) => ({
                                color: theme.palette.primary.main,
                            })}
                        >
                            {productConfiguration.product.name.de}
                        </Typography>
                    </Typography>

                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <TabList onChange={handleTabChange}>
                            <Tab
                                label='Allgemeine Konfiguration'
                                value='general'
                            />
                            <Tab
                                label='Regelwerk Verfügbarkeit'
                                value='contingentCondition'
                            />
                            <Tab
                                label='Regelwerk Preis'
                                value='priceCondition'
                            />
                        </TabList>
                    </Box>

                    <TabPanel value='general'>
                        <ProductConfigurationGeneralConfiguration
                            baseConfiguration={productConfiguration}
                        />
                    </TabPanel>

                    <TabPanel value='contingentCondition'>
                        <ContingentConditionConfiguration
                            productConfiguration={productConfiguration}
                        />
                    </TabPanel>

                    <TabPanel value='priceCondition'>
                        <PriceConditionConfiguration
                            productConfiguration={productConfiguration}
                        />
                    </TabPanel>
                </Stack>
            )}
        </TabContext>
    );
};
